import { FunctionComponent, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import ModalFooter from "../../../../shared/modalFooter"
import { useMutation } from "@apollo/client"
import {
    ManageSubscriptionFormI,
    ManageSubscriptionSubcriberOptionsT,
    ManageSubscriptionViewerActionT,
    UpdateSubscriptionFormI,
} from "../manageSubscriptionFormUtil/types"
import MutationErrorBanner from "../../../../shared/graphQlResponse"
import ManageSubscriptionForm, {
    ManageSubscriptionFormType,
    ManageSub_UpdateAdmin_CompI,
    ManageSub_Update_CompI,
} from "../form"
import { getSubscriptionUpdateMutationVariables } from "../manageSubscriptionFormUtil/api"
import { MultiValue } from "react-select"
import { LabelValueOptionType } from "../../../../../types/commonTypes"
import { SubscriptionUpdateQryFragment } from "../../../../../generated/graphql"
import { SUBSCRIPTION_UPDATE } from "../../../../../graphql/mutations/subscriptions_update"
import { getUpdateSubscriptionFormFromSubscriptionFrag } from "../../subscriptions_GraphQL/subscriptionsFragment"
import { ReactMultiSelectOptionT } from "../../../../shared/input/react-select/multiSelect"
import { parseNumberInput } from "../../../../../util/InputValidation/inputValidation"
import { DurationUnitT } from "../../../../../util/duration/types"
import ManageSubscriptionsActions from "../../manageSubscriptions/actions"
import { navigateAfterMutation } from "../../../navigate"
import { getFragmentData } from "../../../../../generated"
import { PHONE_NUMBER_FRAGMENT } from "../../../../../graphql/fragments/telephoneNumber/telephoneNumber"
import { getPhoneNumber } from "../../../../../graphql/fragments/telephoneNumber/util"

interface UpdateSubscriptionFormComponentI {
    subscription: SubscriptionUpdateQryFragment
    targetMembershipWellPermissions: LabelValueOptionType[]
    viewerAction: ManageSubscriptionViewerActionT
}

const UpdateSubscriptionForm: FunctionComponent<
    UpdateSubscriptionFormComponentI
> = ({ subscription, targetMembershipWellPermissions, viewerAction }) => {
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const [updateSubscriptionMutation, { loading: mutationLoading }] =
        useMutation(SUBSCRIPTION_UPDATE)

    const formTypeIs: ManageSubscriptionFormType =
        viewerAction === "admin" ? "UpdateAdmin" : "UpdateSelf"

    // form state
    const initialForm = getUpdateSubscriptionFormFromSubscriptionFrag(
        subscription,
        targetMembershipWellPermissions
    )

    const [subscriptionForm, setSubscriptionForm] =
        useState<UpdateSubscriptionFormI>(initialForm)

    const [mutationErrorBanner, setMutationErrorBanner] =
        useState<boolean>(false)

    // handlers:
    // newValue is an array of the wells that are selected in the multi select component
    const handleSelectedWellsChange = (
        newValue: MultiValue<ReactMultiSelectOptionT>
    ) => {
        const updatedWellsOptions = subscriptionForm.subscriptionWells.map(
            (well) => {
                const isSelected = newValue.some(
                    (option) => option.value === well.value
                )
                return { ...well, isSelected }
            }
        )
        setSubscriptionForm({
            ...subscriptionForm,
            subscriptionWells: updatedWellsOptions,
        })
    }

    const handleUpdateSubscriptionPreferences = (
        inputId: keyof ManageSubscriptionFormI,
        value: boolean
    ) => {
        setSubscriptionForm({
            ...subscriptionForm,
            [inputId]: value,
        })
    }

    const handleSelectAllWells = (updatedSelect: boolean) => {
        const updatedWellOptions = subscriptionForm.subscriptionWells.map(
            (well) => {
                return { ...well, isSelected: updatedSelect }
            }
        )
        setSubscriptionForm({
            ...subscriptionForm,
            subscriptionWells: updatedWellOptions,
        })
    }

    const handleSilenceValueChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = parseNumberInput(e)
        setSubscriptionForm({
            ...subscriptionForm,
            silenceForm: {
                ...subscriptionForm.silenceForm,
                timeValue: value,
            },
        })
    }

    const handleSilenceUnitChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const targetUnit = e.target.value as DurationUnitT
        setSubscriptionForm({
            ...subscriptionForm,
            silenceForm: {
                ...subscriptionForm.silenceForm,
                timeUnit: targetUnit,
            },
        })
    }

    // submit handlers
    const handleSubmitUpdateSubscription = () => {
        const mutationVariables =
            getSubscriptionUpdateMutationVariables(subscriptionForm)

        updateSubscriptionMutation({
            variables: {
                AlertSubscriptionUpdateInput: mutationVariables,
            },
        })
            .then(() => {
                navigate(navTo.to, navTo.options)
            })
            .catch((error) => {
                console.error("Update subscription mutation error:", error)
                setMutationErrorBanner(true)
            })
    }

    // constants
    const navTo = navigateAfterMutation(pathname)
    const ph = getFragmentData(
        PHONE_NUMBER_FRAGMENT,
        subscription.subscriber.identity?.telephoneNumber
    )
    const displayPhoneNumber = getPhoneNumber(ph)

    const selectedSubscriber: ManageSubscriptionSubcriberOptionsT = {
        name: subscription.subscriber.identity?.fullName ?? "",
        subscriberId: subscription.subscriber.id,
        email: subscription.subscriber.identity?.email,
        phone: displayPhoneNumber,
        emailVerified: subscription.subscriber.identity?.emailVerified ?? false,
        emailConsent: subscription.subscriber.identity?.emailConsent ?? false,
        phoneVerified:
            subscription.subscriber.identity?.telephoneNumberVerified ?? false,
        phoneConsent: subscription.subscriber.identity?.smsConsent ?? false,
        subscriberWellPermissions: subscription.subscribedWells.map((well) => ({
            label: well.name,
            value: well.id,
        })),
        isManagedMember:
            subscription.subscriber.organizationMemberships[0].managedAccess,
    }

    // build props
    const props = {
        ruleOptions: [subscriptionForm.rule],
        handleSelectedWellsChange,
        handleUpdateSubscriptionPreferences,
        handleSelectAllWells,
        handleSilenceUnitChange,
        handleSilenceValueChange,
        form: subscriptionForm,
        selectedSubscriber,
    }

    let manageSubscriptionFormProps:
        | ManageSub_UpdateAdmin_CompI
        | ManageSub_Update_CompI

    switch (formTypeIs) {
        case "UpdateSelf":
            manageSubscriptionFormProps = {
                ...props,
                formType: formTypeIs,
            }
            break
        case "UpdateAdmin":
            manageSubscriptionFormProps = {
                ...props,
                formType: formTypeIs,
                subscriberOptions: [
                    {
                        label: selectedSubscriber.name,
                        value: selectedSubscriber.subscriberId,
                    },
                ],
            }
            break
    }

    const selectedWells = subscriptionForm.subscriptionWells.filter((well) => {
        return well.isSelected
    })

    return (
        <>
            <ManageSubscriptionForm {...manageSubscriptionFormProps} />
            {!mutationErrorBanner && (
                <ManageSubscriptionsActions
                    formType={manageSubscriptionFormProps.formType}
                    alertRuleId={subscriptionForm.rule.value}
                    selectedSubscribedWells={selectedWells}
                />
            )}
            {mutationErrorBanner && (
                <>
                    <MutationErrorBanner
                        message={
                            <div>
                                There was problem updating your subscription.
                                Click{" "}
                                <u
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                        navigate(navTo.to, navTo.options)
                                    }
                                >
                                    here
                                </u>{" "}
                                or close the modal to be redirected.
                            </div>
                        }
                    />
                </>
            )}
            <ModalFooter
                advanceText="Submit"
                disableAdvance={mutationLoading || mutationErrorBanner}
                handleAdvanceClick={handleSubmitUpdateSubscription}
                disablePrevious={mutationLoading}
                handlePreviousClick={() => navigate(-1)}
                previousButtonCondition="default"
                previousText="Cancel"
            />
        </>
    )
}

export default UpdateSubscriptionForm
